import { ActionCreator, AjaxDispatcher } from "./ajax-dispatcher";
import { displayError, getApiUrl, getBaseUrl, getCurrentLanguageId, localize, QueryParameter } from "./common";
import ConfirmDialog from "./confirm-dialog";
import HttpClient from "./http-client";
import LoadingIndication from "./loading-indication";
import {saveAs} from 'file-saver'
import { initTronDesigner, initTronDesignerCartActions } from "./tron-designer/tron-designer";

interface OrderItem {
    id: string;
    removeButton: HTMLButtonElement;
}

export default class OrderDetailEdit {
    private baseElement: HTMLElement;
    private topLevelOrderItems: OrderItem[];
    private approveOrderButton: HTMLButtonElement;
    private downloadOrderButton: HTMLButtonElement;
    private orderGuid: string;

    constructor() {
        this.baseElement = document.querySelector('[data-component="OrderItems"]') as HTMLElement;
        this.orderGuid = this.getOrderGuid();
        this.approveOrderButton = <HTMLButtonElement>document.getElementById('confirm-order-button');
        this.downloadOrderButton = <HTMLButtonElement>document.getElementById('download-order-button');

        this.topLevelOrderItems = this.createOrderItems(
            this.baseElement.querySelectorAll('div[data-order-item="RootItemInHierarchy"]')
        );
    }

    public init(): void {
        this.registerRemoveItemListener();
        this.registerDownloadButtonListener();
        this.registerOrderApproveListener();
        initTronDesigner();
        initTronDesignerCartActions();
    }

    private registerRemoveItemListener(): void {
        this.topLevelOrderItems.forEach(item => {
            item.removeButton &&
                item.removeButton.addEventListener('click', async () => {
                    const confirmDialog = new ConfirmDialog(async () => {
                        LoadingIndication.show(this.baseElement);
                        await this.removeOrderItem(item.id);
                    });

                    const queryParam: QueryParameter = {
                        key: 'confirmDialogViewName',
                        value: 'ConfirmDeleteOrderItem'
                    };

                    confirmDialog.open([queryParam]);
                });
        });
    }

    private registerOrderApproveListener(): void {
        if (!this.approveOrderButton) {
            return;
        }

        this.approveOrderButton.addEventListener("click", async () => {
            await this.approveOrder();
        });
    }

    private registerDownloadButtonListener(): void {
        if (!this.downloadOrderButton) {
            return;
        }

        this.downloadOrderButton.addEventListener("click", async () => {
            await this.downloadOrderPdf();
            LoadingIndication.hide(this.baseElement);
        });
    }

    private async removeOrderItem(itemId: string): Promise<void> {
        const url = `${getApiUrl()}/OrderItem/deleteWithSubitems?orderGuid=${this.orderGuid}&orderItemId=${itemId}`;
        let refused = false;
        const response = await new HttpClient().fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        }, (error: Response | string) => {
            // 404/409: the order or item is gone or can no longer be edited (approved meanwhile, last product
            // line, delivery and payment). Say so and reload the cards to show the real state.
            const status = typeof error === 'string' ? 0 : error?.status;
            refused = status === 404 || status === 409;
            displayError(localize(refused ? 'OrderItemRemoveRefused' : 'AjaxCallFailGeneralMessage'));
        });

        if ((response && response.ok) || refused) {
            this.reloadComponent('OrderItems');
            this.reloadComponent('OrderInformation');
        }

        LoadingIndication.hide(this.baseElement);
    }

    private async approveOrder(): Promise<void> {
        const url = `${getBaseUrl()}orders/approve/${this.getOrderGuid()}`;
        const response = await new HttpClient().fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (response.ok) {
            window.location.reload();
        }
    }

    private reloadComponent(component: string): void {
        const action = new ActionCreator().reloadComponent(component);

        action.successAction = () => {
            const orderDetailEdit = new OrderDetailEdit();
            orderDetailEdit.init();
        };

        new AjaxDispatcher().dispatchGetAction(action);
    }

    private createOrderItems(inputElements: NodeList): OrderItem[] {
        const array: HTMLInputElement[] = Array.prototype.slice.call(
            inputElements
        );

        const productCartItems: OrderItem[] = array.map(item => {
            return {
                id: item.getAttribute('data-order-item-id'),
                removeButton: item.querySelector('[data-action="remove"]')
            } as OrderItem;
        });

        return productCartItems;
    }

    private async downloadOrderPdf(): Promise<void> {
        LoadingIndication.show(this.baseElement);

        const url = `${getApiUrl()}/file/orderpdf/${this.orderGuid}`;
        const languageId = getCurrentLanguageId();
        const response = await new HttpClient().fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': languageId
            },
        });

        LoadingIndication.hide(this.baseElement);

        if (response || response.ok) {
            await this.onSuccess(response);
        }
        else {
            if (response.status == 404) {
                displayError('Order was not found!');
                return;
            }

            displayError('Unexpected error');
        }
    }

    private async onSuccess(response: Response): Promise<void> {
        const blob = await response.blob();
        saveAs(blob);
    }

    private getOrderGuid(): string {
        return this.baseElement.getAttribute('data-order-guid');
    }
}
